import {
  combineLatest as observableCombineLatest,
  of as observableOf,
  Observable,
  Subscription,
  Subject,
  BehaviorSubject,
  ConnectableObservable,
} from 'rxjs';

import {map, shareReplay, catchError, publishReplay, distinctUntilChanged} from 'rxjs/operators';
import {DetailService} from './detail.service';

export class DetailTabconfig {
  private _Name: Observable<ValuesObject<string>>;
  private _IsHeader: Observable<boolean> = observableOf(false);
  private _Caption: Observable<ValuesObject<string>>;
  private _Enabled: Observable<ValuesObject<string>> = observableOf(new ValuesObject('true'));
  private _Visible: Observable<ValuesObject<string>> = observableOf(new ValuesObject('true'));
  private _VisibleControlList: Observable<boolean>[] = [];
  private _Description: Observable<ValuesObject<string>>;
  private _HasChildren: Observable<boolean> = observableOf(false);
  private _Subscriptions: Subscription[] = [];
  Controls: DetailControl[] = [];

  get Name(): Observable<ValuesObject<string>> {
    return this._Name;
  }

  setName(name: Observable<ValuesObject<string>>) {
    const conobs = name.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Name = conobs;
  }

  pushControlVisible(visible: Observable<boolean>) {
    visible = visible.pipe(
      catchError(() => observableOf(false)),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this._VisibleControlList.push(visible);
  }

  get Description(): Observable<ValuesObject<string>> {
    return this._Description;
  }

  setDescription(description: Observable<ValuesObject<string>>) {
    const conobs = description.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Description = conobs;
  }

  get Caption(): Observable<ValuesObject<string>> {
    return this._Caption;
  }

  setCaption(caption: Observable<ValuesObject<string>>) {
    const conobs = caption.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Caption = conobs;
  }

  get HasChilds(): Observable<boolean> {
    return this._HasChildren;
  }

  setHasChilds(hasChildren: Observable<boolean>) {
    const conobs = hasChildren.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._HasChildren = conobs;
  }

  get IsHeader(): Observable<boolean> {
    if (this._IsHeader == null) return null;
    return this._IsHeader;
  }

  setIsHeader(value: Observable<ValuesObject<string>>) {
    const conobs = value.pipe(
      map(valObj => DetailService.AsBoolean(valObj) === true),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._IsHeader = conobs;
  }

  get Enabled(): Observable<boolean> {
    if (this._Enabled == null) return null;
    return this._Enabled.pipe(map(valObj => DetailService.AsBoolean(valObj) != false));
  }

  setEnabled(value: Observable<ValuesObject<string>>) {
    const conobs = value.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Enabled = conobs;
  }

  get Visible(): Observable<boolean> {
    if (this._Visible == null) return null;
    return observableCombineLatest(
      observableCombineLatest(this._VisibleControlList).pipe(
        map((bools: boolean[]) => {
          let finalBool = false;
          if (bools != null && Array.isArray(bools) && bools.length > 0) {
            bools.forEach(bool => {
              if (bool === true) {
                finalBool = true;
              }
            });
          } else {
            finalBool = true;
          }
          return finalBool;
        }),
      ),
      this._Visible.pipe(map(valObj => DetailService.AsBoolean(valObj) == true)),
      (controls, tab) => {
        return controls && tab;
      },
    ).pipe(distinctUntilChanged(), shareReplay(1));
  }

  setVisible(value: Observable<ValuesObject<string>>) {
    const conobs = value.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Visible = conobs;
  }

  destroy() {
    this._Subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
    this.Controls.forEach((control: DetailControl) => {
      control.destroy();
    });
  }
}

export class DetailControl {
  private _Subscriptions: Subscription[] = [];
  private _Name: Observable<ValuesObject<string>>;
  get Name(): Observable<ValuesObject<string>> {
    return this._Name;
  }

  setName(name: Observable<ValuesObject<string>>) {
    const conobs = name.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Name = conobs;
  }

  pushSubscription(subscription: Subscription) {
    this._Subscriptions.push(subscription);
  }

  private _TypeName: Observable<string>;
  get TypeName(): Observable<string> {
    return this._TypeName;
  }

  setTypeName(typename: Observable<ValuesObject<string>>) {
    const conobs = typename.pipe(
      map(valObj => valObj.Values),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._TypeName = conobs;
  }

  private _Caption: Observable<ValuesObject<string>>;
  get Caption(): Observable<ValuesObject<string>> {
    return this._Caption;
  }

  setCaption(caption: Observable<ValuesObject<string>>) {
    const conobs = caption.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Caption = conobs;
  }

  private _Description: Observable<ValuesObject<string>>;
  get Description(): Observable<ValuesObject<string>> {
    return this._Description;
  }

  setDescription(description: Observable<ValuesObject<string>>) {
    const conobs = description.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Description = conobs;
  }

  private _Enabled: Observable<boolean>;
  private _FinalEnabled: Observable<boolean>;

  get Enabled(): Observable<boolean> {
    return this._FinalEnabled;
  }

  get BasicEnabled(): Observable<boolean> {
    return this._Enabled;
  }

  setFinalEnabled(enabled: Observable<boolean>) {
    const conobs = enabled.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._FinalEnabled = conobs;
  }

  setEnabled(enabled: Observable<ValuesObject<any>>) {
    const conobs = enabled.pipe(
      map(valObj => DetailService.AsBoolean(valObj) != false),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Enabled = conobs;
  }

  private _ExpandArea: Observable<boolean>;
  get ExpandArea(): Observable<boolean> {
    return this._ExpandArea;
  }

  setExpandArea(expandarea: Observable<ValuesObject<any>>) {
    const conobs = expandarea.pipe(
      map(valObj => DetailService.AsBoolean(valObj) == true),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._ExpandArea = conobs;
  }

  _Visible: Observable<boolean>;
  get Visible(): Observable<boolean> {
    return this._Visible;
  }

  setVisible(visible: Observable<ValuesObject<any>>) {
    const conobs = visible.pipe(
      map(valObj => DetailService.AsBoolean(valObj) != false),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Visible = conobs;
  }

  private _Hint: Observable<ValuesObject<string>>;
  get Hint(): Observable<ValuesObject<string>> {
    return this._Hint;
  }

  setHint(hint: Observable<ValuesObject<string>>) {
    const conobs = hint.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Hint = conobs;
  }

  private _RightsLevel: Observable<string[]>;
  get RightsLevel(): Observable<string[]> {
    return this._RightsLevel;
  }

  ReadPermission: Observable<boolean> = observableOf(false);
  ModifyPermission: Observable<boolean> = observableOf(false);
  AddPermission: Observable<boolean> = observableOf(false);
  RemovePermission: Observable<boolean> = observableOf(false);

  setRightsLevel(rightslevel: Observable<string[]>) {
    const conobs = rightslevel.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._RightsLevel = conobs;
    this.ReadPermission = conobs.pipe(
      map(strArr => {
        if (strArr == null || !Array.isArray(strArr)) {
          // Muss inaktiv sein wenn falsches binding, oder attribut nicht sichtbar, ist true wenn kein rightslevel (detail.service)
          return false;
        }
        return strArr.indexOf('Read') > -1;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.ModifyPermission = conobs.pipe(
      map(strArr => {
        if (strArr == null || !Array.isArray(strArr)) {
          // Muss inaktiv sein wenn falsches binding, oder attribut nicht sichtbar, ist true wenn kein rightslevel (detail.service)
          return false;
        }
        return strArr.indexOf('Modify') > -1;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.AddPermission = conobs.pipe(
      map(strArr => {
        if (strArr == null || !Array.isArray(strArr)) {
          // Muss inaktiv sein wenn falsches binding, oder attribut nicht sichtbar, ist true wenn kein rightslevel (detail.service)
          return false;
        }
        return strArr.indexOf('Add') > -1;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.RemovePermission = conobs.pipe(
      map(strArr => {
        if (strArr == null || !Array.isArray(strArr)) {
          // Muss inaktiv sein wenn falsches binding, oder attribut nicht sichtbar, ist true wenn kein rightslevel (detail.service)
          return false;
        }
        return strArr.indexOf('Remove') > -1;
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  hasErrors: boolean = false;

  Options: DetailControlOption[];
  private Properties: Observable<ValuesObject<any>>[];
  private PropertyValues: Observable<any>[];

  setProperties(value: Observable<ValuesObject<any>>[]) {
    this.Properties = value;
  }

  setPropertyValues(value: Observable<any>[]) {
    this.PropertyValues = value;
  }

  propertyExists(name: string): boolean {
    return !!(
      this.Properties != null &&
      Array.isArray(this.Properties) &&
      this.Properties[name] != null
    );
  }

  hasProperties(): boolean {
    return !(this.Properties == null || !Array.isArray(this.Properties));
  }

  getPropertyValues(name: string): Observable<any> {
    if (this.propertyExists(name)) {
      return this.PropertyValues[name];
    }
    return observableOf(null);
  }

  getProperty(name: string): Observable<ValuesObject<any>> {
    if (this.propertyExists(name)) {
      return this.Properties[name].pipe(distinctUntilChanged(), shareReplay(1));
    } else {
      return observableOf(null);
    }
  }

  getBooleanPropertyValues(name: string): Observable<boolean> {
    return this.getPropertyValues(name).pipe(
      map(val => val != null && (val === 'true' || val === 'True' || val === true)),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  destroy() {
    this._Subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
    this.Options.forEach((option: DetailControlOption) => {
      option.destroy();
    });
  }
}

export class DetailControlOption {
  private _Subscriptions: Subscription[] = [];
  private _Value: Observable<ValuesObject<any>>;
  get Value(): Observable<ValuesObject<any>> {
    return this._Value;
  }

  static readonly NullValueOverwrite = '↠↦NULL↤↞';

  setValue(value: Observable<ValuesObject<any>>) {
    const conobs = value.pipe(
      map((vo: ValuesObject<any>) => {
        if (vo.Values === '' || vo.Values == null)
          vo.Values = DetailControlOption.NullValueOverwrite;
        return vo;
      }),
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Value = conobs;
  }

  private _Caption: Observable<ValuesObject<string>>;
  get Caption(): Observable<ValuesObject<string>> {
    return this._Caption;
  }

  setCaption(caption: Observable<ValuesObject<string>>) {
    const conobs = caption.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Caption = conobs;
  }

  private _Hint: Observable<ValuesObject<string>>;
  get Hint(): Observable<ValuesObject<string>> {
    return this._Hint;
  }

  setHint(hint: Observable<ValuesObject<string>>) {
    const conobs = hint.pipe(
      distinctUntilChanged(),
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._Subscriptions.push(conobs.connect());
    this._Hint = conobs;
  }

  destroy() {
    this._Subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}

export class ValuesObject<T> {
  constructor(
    public Values: T,
    public PermissionHint?: string[],
    public BindingSource?: string,
    public BindingPath?: string,
    public Locale?: string,
    public formatSave?: (value: any) => any, // used for xml-Filter and multi-value conversion
    public SpecialObject?: any,
  ) {}

  public clone(): ValuesObject<T> {
    return new ValuesObject(
      this.Values,
      this.PermissionHint,
      this.BindingSource,
      this.BindingPath,
      this.Locale,
      this.formatSave,
      this.SpecialObject,
    );
  }
}
